import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { formatDistanceStrict } from 'date-fns';
import React from 'react';

import { useCreateFakeSpeiPaymentMutation } from '@/api/v1';
import Copy from '@/assets/icons/copy.svg?react';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { SHOULD_MOCK_DEPOSITS } from '@/constants';
import { useCustomer } from '@/hooks/useCustomer';
import { useToast } from '@/hooks/useToast';
import { LocaleContext, useTranslator } from '@/hooks/useTranslator';
import { getCustomerTaxId } from '@/types/customer';
import { LabelAndValue } from '@/types/labelAndValue';
import { Locale } from '@/types/locale';
import { toDateFnsLocale } from '@/utility/toDateFnsLocale';

interface Props {
  readonly clabe: string;
  readonly bankName: string;
  readonly beneficiaryName: string;
  readonly expiresAt: string;
  readonly transactionId: string;
}

export const SpeiClabeSection: React.FC<Props> = (props: Props): React.ReactElement => {
  const { clabe, bankName, beneficiaryName, expiresAt, transactionId } = props;
  const toast = useToast();
  const tr = useTranslator();
  const locale = React.useContext<Locale>(LocaleContext);
  const customer = useCustomer();

  const [createFakeSpeiTransaction] = useCreateFakeSpeiPaymentMutation();
  const [isCopying, setIsCopying] = React.useState<boolean>(false);

  const copyToClipboard = React.useCallback(async (): Promise<void> => {
    if (SHOULD_MOCK_DEPOSITS) {
      const taxId = getCustomerTaxId(customer);

      createFakeSpeiTransaction({
        type: 'SPEI',
        transactionId: transactionId,
        documentId: taxId,
      });
    } else {
      const { clipboard } = navigator;

      setIsCopying(true);
      try {
        await clipboard.writeText(clabe);
        toast.show(tr('SPEI_CLABE_COPIED'), 'success');
      } catch {
        toast.show(tr('AN_ERROR_OCCURRED'), 'error');
      } finally {
        setIsCopying(false);
      }
    }
  }, [clabe, createFakeSpeiTransaction, customer, toast, tr, transactionId]);

  const entries = React.useMemo(
    (): readonly LabelAndValue[] => [
      {
        label: 'CLABE',
        value: clabe,
      },
      {
        label: tr('SPEI_BANK_BENEFICIARY'),
        value: beneficiaryName,
      },
      {
        label: tr('SPEI_BANK_NAME'),
        value: bankName,
      },
    ],
    [bankName, beneficiaryName, clabe, tr],
  );

  const expirationFormatted = React.useMemo((): string | null => {
    const expirationDate = new Date(expiresAt);

    if (isNaN(expirationDate.getTime())) {
      return null;
    }

    if (expirationDate.getTime() <= Date.now()) {
      return null;
    }

    return formatDistanceStrict(expirationDate, new Date(), {
      unit: 'hour',
      roundingMethod: 'ceil',
      locale: toDateFnsLocale(locale),
    });
  }, [locale, expiresAt]);

  return (
    <Box display="flex" flexDirection="column" gap={3} py={2}>
      <Box sx={styles.codeContainer}>
        <Box sx={styles.codeStringAndButtonContainer}>
          <Box sx={styles.codeStringBox}>
            {entries.map((entry: LabelAndValue): React.ReactNode => {
              return (
                <Typography key={entry.label} fontSize={14} fontWeight={400} lineHeight="24px">
                  <Typography
                    component="span"
                    fontSize="inherit"
                    fontWeight={600}
                    lineHeight="inherit"
                    whiteSpace="nowrap">
                    {entry.label}:{' '}
                  </Typography>
                  <Typography
                    sx={styles.codeString}
                    component="span"
                    fontSize="inherit"
                    fontWeight="inherit"
                    lineHeight="inherit">
                    {entry.value}
                  </Typography>
                </Typography>
              );
            })}
          </Box>
          <LoadingButton
            variant="dark"
            size="compact"
            startIcon={<Copy fill="currentColor" />}
            loading={isCopying}
            onClick={copyToClipboard}>
            {tr('COPY_CLABE')}
          </LoadingButton>
        </Box>
      </Box>
      {expirationFormatted && (
        <Box textAlign="center">
          <Typography fontSize={16} fontWeight={400}>
            <Typography fontSize="inherit" fontWeight="inherit" component="span">
              {tr('QUOTE_WILL_EXPIRE')}{' '}
            </Typography>
            <Typography
              fontSize="inherit"
              fontWeight="inherit"
              component="span"
              color="primary.main">
              {expirationFormatted}
            </Typography>
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  codeContainer: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'custom.Primary1Background',
    padding: 4,
    gap: 3,
    borderRadius: 3,
  },
  codeString: {
    wordBreak: 'break-word',
  },
  codeStringBox: {
    flex: 1,
    mb: 0,
    minWidth: 0,
  },
  codeStringAndButtonContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    minWidth: 0,
    flex: 1,
  },
};
